import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { useTranslation } from '@/src/i18n';
import { createStyles } from './CreateTeamScreen.styles';

interface CreateTeamScreenProps {
  name: string;
  description: string;
  logoUrl: string | null;
  isPublic: boolean;
  creating: boolean;
  uploadingLogo: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onIsPublicChange: (value: boolean) => void;
  onUploadLogo: () => void;
  onCreate: () => void;
  onCancel: () => void;
  colors: ThemeColors;
}

export const CreateTeamScreen: React.FC<CreateTeamScreenProps> = ({
  name,
  description,
  logoUrl,
  isPublic,
  creating,
  uploadingLogo,
  onNameChange,
  onDescriptionChange,
  onIsPublicChange,
  onUploadLogo,
  onCreate,
  onCancel,
  colors,
}) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        {/* Logo */}
        <View style={styles.logoSection}>
          <View style={[styles.logoCircle, { borderColor: colors.border, backgroundColor: colors.surface }]}>
            {logoUrl ? (
              <Image source={{ uri: logoUrl }} style={styles.logoImage} />
            ) : (
              <Ionicons name="people" size={40} color={colors.textTertiary} />
            )}
          </View>
          <TouchableOpacity
            style={[styles.logoButton, { borderColor: colors.border, backgroundColor: colors.surface }]}
            onPress={onUploadLogo}
            disabled={uploadingLogo}
          >
            {uploadingLogo ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <Text style={[styles.logoButtonText, { color: colors.primary }]}>
                {logoUrl ? t('teams.changeLogo') : t('teams.uploadLogo')}
              </Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Name */}
        <Text style={[styles.label, { color: colors.text }]}>{t('teams.teamName')} *</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }]}
          placeholder={t('teams.teamNamePlaceholder')}
          placeholderTextColor={colors.textTertiary}
          value={name}
          onChangeText={onNameChange}
          maxLength={100}
        />

        {/* Description */}
        <Text style={[styles.label, { color: colors.text }]}>{t('teams.description')}</Text>
        <TextInput
          style={[styles.input, styles.textArea, { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }]}
          placeholder={t('teams.descriptionPlaceholder')}
          placeholderTextColor={colors.textTertiary}
          value={description}
          onChangeText={onDescriptionChange}
          multiline
          maxLength={500}
        />

        {/* Visibility */}
        <View style={[styles.visibilityRow, { borderColor: colors.border, backgroundColor: colors.surface }]}>
          <View style={styles.visibilityTextBlock}>
            <Text style={[styles.visibilityLabel, { color: colors.text }]}>{t('teams.public')}</Text>
            <Text style={[styles.visibilityDesc, { color: colors.textSecondary }]}>{t('teams.publicDesc')}</Text>
          </View>
          <Switch
            value={isPublic}
            onValueChange={onIsPublicChange}
            trackColor={{ false: colors.border, true: colors.primary }}
            thumbColor={colors.primaryText}
          />
        </View>

        {/* Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.primaryButton, creating && styles.disabledButton]}
            onPress={onCreate}
            disabled={creating}
          >
            {creating ? (
              <ActivityIndicator color={colors.primaryText} />
            ) : (
              <Text style={styles.primaryButtonText}>{t('common.create')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity style={styles.secondaryButton} onPress={onCancel}>
            <Text style={styles.secondaryButtonText}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};
